import styled from 'styled-components';

import Button from 'components/shared/Button';
import { simpleDateAtTime } from 'lib/date';
import { WorkflowAction } from 'models/Workflow';
import { getEligibilityPdfUrl } from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { LinkButton } from './BenefitsTable';

interface Props {
  workflowId: string;
  // The coverage's verification. Its completion is what "last ran" means — the payer search running
  // again isn't a verification run.
  benefits: WorkflowAction | undefined;
  facetId: string | null;
  // False when the run produced no downloadable document (an error, or a payer that isn't enrolled).
  pdfAvailable: boolean;
  // Omitted for a reader who may not re-run this verification. The footer still renders — when
  // the check last ran and its PDF are readings of the result — it just carries no re-run.
  onRerun?: () => void;
  isRerunning: boolean;
  // The plain-language benefits view's footer: a Download PDF link and a small Re-run button.
  compact?: boolean;
}

// The pinned footer for a coverage: when it last ran and who ran it, the PDF of that run, and the
// re-run. Sits outside the scrolling body (see DetailSlideout) because a benefits result is long
// enough that a footer inside it would only be found by scrolling to the end.
export default function EligibilityFooter({
  workflowId,
  benefits,
  facetId,
  pdfAvailable,
  onRerun,
  isRerunning,
  compact = false,
}: Props) {
  const { addToast } = useToastActions();

  const downloadPdf = () => {
    // A fresh short-lived signed URL per click, scoped to this coverage — no client-side generation.
    getEligibilityPdfUrl(workflowId, facetId)
      .then(({ url }) => window.open(url, '_blank'))
      .catch(() => addToast({ text: 'Could not download the PDF. Please try again.' }));
  };

  const lastRan = benefits?.completedAt
    ? `Last ran on ${simpleDateAtTime(benefits.completedAt)}${
        benefits.completedByName ? ` by ${benefits.completedByName}` : ''
      }`
    : '';

  // Only once there's something to re-run. A coverage waiting on its first check used to get this
  // button disabled, which put a dead control directly under the live "Run Eligibility Check" on the
  // payer-selection panel — two buttons, one of which could never be pressed. What that coverage is
  // waiting on is a payer or a member ID, and the panel above says so.
  const canRerun = Boolean(benefits?.completedAt && benefits.rerunnable && onRerun);

  // Each half is absent rather than empty when it has nothing to show. The slideout stacks this
  // footer on a phone and gives every child the full width, so an empty element is a blank row and
  // a gap above it — which is what a completed check with no PDF, read by someone who cannot re-run
  // it, left under "Last ran on…".
  return (
    <>
      {lastRan && <LastRan data-testid='eligibility-last-ran'>{lastRan}</LastRan>}
      {(pdfAvailable || canRerun) && (
        <Actions>
          {pdfAvailable &&
            (compact ? (
              <LinkButton type='button' onClick={downloadPdf} data-testid='download-eligibility-pdf'>
                Download PDF
              </LinkButton>
            ) : (
              <Button variant='outline-primary' onClick={downloadPdf} data-testid='download-eligibility-pdf'>
                Download PDF
              </Button>
            ))}
          {canRerun && (
            <Button
              onClick={onRerun}
              disabled={isRerunning}
              loading={isRerunning}
              variant={compact ? 'outline-primary' : 'primary'}
              size={compact ? 'small' : 'default'}
              data-testid='rerun-eligibility'>
              {compact ? 'Re-run' : 'Re-Run Benefit Verification'}
            </Button>
          )}
        </Actions>
      )}
    </>
  );
}

const LastRan = styled(BodySmall)`
  color: ${colors.black50};
`;

// Right-aligned whether or not "Last ran on…" is beside it — the footer's space-between does that
// only while there are two children. A no-op on a phone, where the footer stacks and gives this the
// full width.
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  /* The slideout stacks the footer below the breakpoint and gives this block the full width; the
     two buttons inside it have to stack as well. Reversed because the re-run is the primary of the
     pair and the mock puts it above the download (Figma 12236:16940). */
  @media ${({ theme }) => theme.devices.mobile} {
    flex-direction: column-reverse;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;
